import { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { useResizeObserver } from './useResizeObserver';

/**
 * Custom hook to track drawer dimensions and position using ResizeObserver
 * Optimized for performance with debouncing and selective updates
 */
export const useDrawerDimensions = (drawerRef, options = {}) => {
  const {
    debounceMs = 16,
    threshold = 5,
    enabled = true,
  } = options;

  const [dimensions, setDimensions] = useState({
    rightPosition: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    isPositioned: false,
  });

  const lastDimensionsRef = useRef(dimensions);

  const calculateDimensions = useCallback(() => {
    if (!enabled || !drawerRef?.current) {
      if (lastDimensionsRef.current.isPositioned) {
        const newDimensions = { ...lastDimensionsRef.current, isPositioned: false };
        lastDimensionsRef.current = newDimensions;
        setDimensions(newDimensions);
      }
      return;
    }
    try {
      const drawerElement = drawerRef.current;
      const rect = drawerElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      const newDimensions = {
        rightPosition: viewportWidth - rect.left + 100,
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top,
        isPositioned: true,
      };

      const hasSignificantChange = Object.keys(newDimensions).some((key) => {
        if (key === 'isPositioned') return false;
        return Math.abs(lastDimensionsRef.current[key] - newDimensions[key]) > threshold;
      });

      if (hasSignificantChange || !lastDimensionsRef.current.isPositioned) {
        lastDimensionsRef.current = newDimensions;
        setDimensions(newDimensions);
      }
    } catch (error) {
      // Handle getBoundingClientRect errors gracefully
      const errorDimensions = { ...lastDimensionsRef.current, isPositioned: false };
      lastDimensionsRef.current = errorDimensions;
      setDimensions(errorDimensions);
    }
  }, [drawerRef, enabled, threshold]);

  const debouncedCalculate = useCallback(
    debounce(calculateDimensions, debounceMs, {
      leading: true,
      trailing: true,
    }),
    [calculateDimensions, debounceMs]
  );

  const handleResize = useCallback((entries, element) => {
    const drawerEntry = entries.find((entry) => entry.target === element);
    if (drawerEntry) {
      debouncedCalculate();
    }
  }, [debouncedCalculate]);

  useResizeObserver(drawerRef, handleResize, { enabled });

  useEffect(() => {
    if (!enabled) return undefined;

    calculateDimensions();
    // Handle window resize
    const handleWindowResize = debouncedCalculate;
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      debouncedCalculate.cancel();
    };
  }, [enabled, calculateDimensions, debouncedCalculate]);

  useEffect(() => {
    if (enabled) {
      calculateDimensions();
    }
  }, [drawerRef, calculateDimensions, enabled]);

  useEffect(() => {
    lastDimensionsRef.current = dimensions;
  }, [dimensions]);

  return dimensions;
};